import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useAlert } from '../../components';
import { getUserProducts, getActiveAnnouncements, getTeamData } from '../../services/firestoreService';
import { formatCurrency } from '../../utils/helpers';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../config/firebase';
import './HomeScreen.css';

export const HomeScreen = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const alert = useAlert();
    const [userProducts, setUserProducts] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isInstalled, setIsInstalled] = useState(false);
    const [teamCount, setTeamCount] = useState(0);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    const defaultBanners = [
        'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=800&h=400&fit=crop',
        'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop',
        'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=400&fit=crop',
    ];

    useEffect(() => {
        // Check if already installed
        if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
            setIsInstalled(true);
        }
        loadData();
    }, []);

    useEffect(() => {
        if (banners.length > 0) {
            const interval = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % banners.length);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [banners]);

    const loadData = async () => {
        try {
            const [products, announcementsData, team] = await Promise.all([
                getUserProducts(),
                getActiveAnnouncements(),
                userData?.uid ? getTeamData(userData.uid, userData.referralCode) : Promise.resolve({ level1: [], level2: [], level3: [] })
            ]);
            setUserProducts(products);
            setAnnouncements(announcementsData.slice(0, 3));
            setTeamCount((team.level1.length || 0) + (team.level2.length || 0) + (team.level3.length || 0));

            // Load banners from Firestore
            const bannersQuery = query(
                collection(db, 'banners'),
                where('isActive', '==', true),
                orderBy('order', 'asc')
            );
            const bannersSnapshot = await getDocs(bannersQuery);
            const bannersData = bannersSnapshot.docs.map(doc => doc.data().imageUrl);

            setBanners(bannersData.length > 0 ? bannersData : defaultBanners);
        } catch (error) {
            console.error('Error loading data:', error);
            setBanners(defaultBanners);
        } finally {
            setLoading(false);
        }
    };

    const totalDailyIncome = userProducts.reduce((sum, p) => sum + (p.dailyIncome || 0), 0);

    const handleTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const minSwipe = 50;
        if (distance > minSwipe) {
            // Swipe left → next
            setCurrentSlide((prev) => (prev + 1) % banners.length);
        } else if (distance < -minSwipe) {
            // Swipe right → prev
            setCurrentSlide((prev) => (prev - 1 + banners.length) % banners.length);
        }
    };

    const handleInstallClick = async () => {
        const promptEvent = window.deferredPrompt;

        if (!promptEvent) {
            if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
                alert.showInfo('Installed', 'NEXTRON is already installed on your device!');
                setIsInstalled(true);
            } else {
                alert.showInfo('Preparing', 'Preparing installation... Please try again in a few seconds or use the "Install App" option in your browser menu.');
            }
            return;
        }

        promptEvent.prompt();
        const { outcome } = await promptEvent.userChoice;
        if (outcome === 'accepted') {
            window.deferredPrompt = null;
            setIsInstalled(true);
            alert.showSuccess('Success', 'Thank you for installing NEXTRON!');
        }
    };

    return (
        <div className="home-screen">
            {/* Banner Slider */}
            <div
                className="banner-slider"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                <div className="banner-images" style={{ transform: `translateX(-${currentSlide * 100}%)` }}>
                    {banners.map((img, index) => (
                        <div key={index} className="banner-slide">
                            <img src={img} alt={`Banner ${index + 1}`} />
                        </div>
                    ))}
                </div>
                <div className="banner-dots">
                    {banners.map((_, index) => (
                        <span
                            key={index}
                            className={`dot ${currentSlide === index ? 'active' : ''}`}
                            onClick={() => setCurrentSlide(index)}
                        />
                    ))}
                </div>
            </div>

            {/* Announcement Section */}
            <div className="announcement-marquee-container" onClick={() => navigate('/notifications')}>
                <div className="marquee-label">
                    <span className="material-icons">campaign</span>
                    <span>NEWS</span>
                </div>
                <div className="marquee-content">
                    <div className="marquee-text">
                        {announcements.length > 0 ? (
                            announcements.map((a, i) => (
                                <span key={i}>{a.title} • {a.message.substring(0, 100)}... • </span>
                            ))
                        ) : (
                            <span>Welcome to NEXTRON! Invest in the future of renewable energy. Stay tuned for latest updates.</span>
                        )}
                    </div>
                </div>
                <span className="material-icons marquee-arrow">chevron_right</span>
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
                <button className="action-btn recharge-btn" onClick={() => navigate('/recharge')}>
                    <span className="material-icons">wallet</span>
                    <span>Recharge</span>
                </button>
                <button className="action-btn withdrawal-btn" onClick={() => navigate('/withdraw')}>
                    <span className="material-icons">sync_alt</span>
                    <span>Withdrawal</span>
                </button>
            </div>

            {/* About Section */}
            <div className="about-section-container">
                <div className="about-header">
                    <h2 className="about-title-new">
                        <span className="about-highlight">About</span> NEXTRON
                    </h2>
                    <button className="view-all-ghost-btn" onClick={() => navigate('/about')}>
                        VIEW ALL
                    </button>
                </div>
                <p className="about-description">
                    NEXTRON is a pioneering global technology investment firm established in 2018. We specialize in financing large-scale renewable energy infrastructure and cutting-edge digi...
                </p>

                {/* Stats Cards Inside About Card */}
                <div className="stats-grid-inner">
                    <div className="stat-item-inner">
                        <span className="stat-inner-label">TEAM MEMBERS</span>
                        <span className="stat-inner-value">{teamCount}</span>
                    </div>
                    <div className="stat-item-inner">
                        <span className="stat-inner-label">TEAM EARNING</span>
                        <span className="stat-inner-value highlight">{formatCurrency(userData?.totalTeamEarnings || 0)}</span>
                    </div>
                </div>
            </div>

            {/* Official App Card */}
            {!isInstalled && (
                <div className="app-download-banner">
                    <div className="app-info-left">
                        <div className="android-icon-box">
                            <span className="material-icons">android</span>
                        </div>
                        <div className="app-text-content">
                            <h3 className="app-title-main">OFFICIAL APP</h3>
                            <p className="app-subtitle-sub">BETTER & FASTER ACCESS</p>
                        </div>
                    </div>
                    <button className="app-download-btn-modern" onClick={handleInstallClick}>
                        DOWNLOAD
                    </button>
                </div>
            )}

            {/* Latest Announcements Section */}
            <div className="latest-announcements-section">
                <div className="section-header-modern">
                    <h3 className="section-title-main">LATEST UPDATES</h3>
                    <button className="view-all-link" onClick={() => navigate('/notifications')}>
                        VIEW ALL
                    </button>
                </div>

                <div className="announcements-list-home">
                    {announcements.map((ann, index) => (
                        <div key={index} className="announcement-home-card" onClick={() => navigate('/notifications')}>
                            <div className="ann-icon-circle">
                                <span className="material-icons">notifications_active</span>
                            </div>
                            <div className="ann-card-content">
                                <h4 className="ann-card-title">{ann.title}</h4>
                                <p className="ann-card-text">{ann.message.substring(0, 80)}...</p>
                                <span className="ann-card-date">{new Date(ann.createdAt?.seconds * 1000).toLocaleDateString()}</span>
                            </div>
                        </div>
                    ))}
                    {announcements.length === 0 && (
                        <div className="empty-ann-state">
                            <p>No recent updates available.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Global Presence */}
            <div className="global-presence-section">
                <h3 className="global-presence-title">GLOBAL PRESENCE</h3>
                <div className="presence-chips-container">
                    <span className="presence-chip">Singapore</span>
                    <span className="presence-chip">Mumbai</span>
                    <span className="presence-chip">London</span>
                    <span className="presence-chip">Dubai</span>
                </div>
            </div>
        </div>
    );
};

export default HomeScreen;
